/* eslint-disable camelcase */
import { compareIgnoreCase, escapeRegex } from '../utils/string-utils';

// the extension stuff lives here otherwise we cause circular dependencies :-\
export const EXT_MATCHER = /\.(\w+)$/;

export function getExtension(filename) {
  return EXT_MATCHER.test(filename)
    ? filename.match(EXT_MATCHER)[1].toLowerCase()
    : '';
}

const frameworks = {
  'clojure.test': 'Clojure clojure.test',
  'cw-2': 'Qualified Testing Framework',
  busted: 'Busted',
  criterion: 'Criterion',
  cw: 'Qualified Testing Framework',
  eunit: 'eunit',
  ex_unit: 'ExUnit',
  fuchu: 'Fuchu',
  ginkgo: 'Ginkgo',
  gtest: 'Google Test',
  hspec: 'hspec Testing Framework',
  igloo: 'Igloo BDD',
  jest: 'Jest',
  junit5: 'JUnit 5',
  junit: 'JUnit',
  karma_bdd: 'Karma + Mocha BDD',
  karma_tdd: 'Karma + Mocha TDD',
  kotlintest: 'KotlinTest',
  mocha_bdd: 'Mocha BDD',
  mocha_tdd: 'Mocha TDD',
  mocha: 'Mocha',
  nunit: 'NUnit',
  ounit: 'OUnit',
  pester: 'Pester',
  phpunit: 'PHPUnit',
  rspec: 'RSpec',
  rust: 'Rust',
  scalatest: 'ScalaTest',
  spek: 'Spek',
  spock: 'Spock',
  test: 'Dart Test',
  testthat: 'testthat',
  truffle_with_mocha: 'Truffle + Mocha BDD',
  unitkit: 'UnitKit',
  unittest: 'Python unittest',
  xctest: 'XCTest',
};

const COMMENT_C_LIKE = ['//', '/*', '*/'];
const COMMENT_SHELL_LIKE = ['#'];
const COMMENT_RUBY_LIKE = ['#', '\n=begin', '\n=end'];
const COMMENT_HTML = ['', '<!--', '-->'];

/**
 * List of languages, with the name or id as the key for each language.
 *
 * Languages can have the following properties:
 *
 * - n: Name (if the name does not make for a good key [C#, F#])
 * - x: extension or list of extensions, defaults to lowercase id if not provided
 * - m: codemirror type or mimetype, defaults to lowercase id if not provided, set to false to exclude
 * - c: set to false if not a Challenge language,
 * - f: framework or frameworks
 * - p: true if a primary language
 * - h: highlight language, if not the id
 * - cm: comments mode (see constants above)
 * - ac: enable autocomplete
 * - filenames: Special filenames that map back onto this mode (e.x., files without extensions)
 * - l: Language Id override for Language Server if necessary.
 * - ls: Language Servers.
 *
 * Note: this is all compacted like this to make it take up less space in the final form
 *
 * Given the most basic form, such as Java:{}, The resulting object will look like:
 *
 * {
 *   id: 'java',
 *   name: 'Java',
 *   ext: ['java'],
 *   codemirror: 'java'
 *   challenge: true,
 *   highlight: 'java',
 *   primary: false,
 *   cm: ['//', '/*', '*\/'],
 * }
 *
 * The language will be reinserted into the hash based on it's lowercase name, id, and all extensions,
 * on a first-come, first-serve basis.
 *
 * @type {{[NameOrId]: {n: string, x: string[], m:string}}}
 */
export const languages = {
  C: {
    x: ['c', 'h'],
    m: 'text/x-csrc',
    f: ['criterion'],
  },
  Clojure: {
    x: ['clj', 'cljs'],
    f: ['clojure.test'],
    C: [';'],
  },
  CoffeeScript: {
    x: 'coffee',
    f: ['cw-2'],
    cm: ['#', '###', '###'],
  },
  cpp: {
    n: 'C++',
    x: ['cc', 'cpp', 'hpp'],
    m: 'text/x-c++src',
    f: ['gtest', 'igloo'],
    p: true,
  },
  CSharp: {
    n: 'C#',
    x: 'cs',
    m: 'text/x-csharp',
    f: ['nunit'],
    p: true,
    ls: ['omnisharp-roslyn'],
  },
  css: {
    n: 'CSS3',
    m: 'text/css',
    c: false,
    ac: true,
    ls: ['css-language-server'],
  },
  Dart: {
    x: 'dart',
    f: ['test'],
  },
  Elixir: {
    x: ['ex', 'exs'],
    f: ['ex_unit'],
    m: 'ruby',
    cm: COMMENT_RUBY_LIKE,
  },
  Erb: {
    x: 'erb',
    m: 'application/x-erb',
    c: false,
  },
  Erlang: {
    x: 'erl',
    f: ['eunit'],
    m: 'text/x-erlang',
    cm: ['%'],
  },
  FSharp: {
    n: 'F#',
    x: 'fs',
    m: 'text/x-fsharp',
    f: ['nunit', 'fuchu'],
    cm: ['//', '(*', '*)'],
  },
  Go: {
    m: 'text/x-go',
    f: ['ginkgo'],
  },
  Groovy: {
    x: ['groovy'],
    f: ['junit5', 'junit', 'spock'],
    m: 'text/x-groovy',
  },
  Haskell: {
    x: ['hs', 'lhs'],
    f: ['hspec'],
    cm: ['--'],
  },
  HTML: {
    x: ['htm', 'html'],
    m: 'text/html',
    c: false,
    cm: COMMENT_HTML,
    ac: true,
    ls: ['html-language-server'],
  },
  Java: {
    m: 'text/x-java',
    f: ['junit5', 'junit'],
    p: true,
    ls: ['jdt-language-server'],
  },
  JavaScript: {
    x: ['js', 'json'],
    f: ['jest', 'mocha_bdd', 'mocha_tdd', 'karma_bdd', 'karma_tdd', 'cw-2'],
    m: 'text/jsx',
    p: true,
    ls: ['typescript-language-server', 'vue-language-server'],
  },
  JSP: {
    x: 'jsp',
    m: 'application/x-jsp',
    ac: true,
    c: false,
  },
  JSX: {
    m: 'text/jsx',
    c: false,
    ac: true,
    h: 'javascript',
    l: 'javascriptreact',
    ls: ['typescript-language-server'],
  },
  Kotlin: {
    x: ['kt', 'kts'],
    f: ['junit5', 'junit', 'spek', 'kotlintest'],
    m: 'text/x-kotlin',
  },
  Less: {
    m: 'text/x-less',
    n: 'LessCSS',
    ac: true,
    c: false,
    ls: ['css-language-server'],
  },
  Lua: {
    x: ['lua'],
    f: ['busted'],
    m: 'text/x-lua',
  },
  Markdown: {
    x: ['md', 'mark', 'markdown'],
    m: 'better-markdown',
    c: false,
    cm: COMMENT_HTML,
  },
  ObjC: {
    n: 'Objective-C',
    x: ['m', 'mm'],
    m: 'text/x-objectivec',
    f: ['unitkit', 'cw'],
  },
  OCaml: {
    m: 'text/x-ocaml',
    x: 'ml',
    f: ['ounit'],
    cm: ['', '(*', '*)'],
  },
  PHP: {
    x: ['php', 'php3'],
    f: ['phpunit'],
    m: 'text/x-php',
    p: true,
  },
  PowerShell: {
    x: ['ps1', 'psm1'],
    f: ['pester'],
    m: 'application/x-powershell',
    cm: COMMENT_SHELL_LIKE,
  },
  Python: {
    x: 'py',
    f: ['unittest', 'cw-2'],
    p: true,
    cm: COMMENT_SHELL_LIKE,
    ls: ['pyright-langserver'],
  },
  R: {
    x: 'r',
    f: ['testthat'],
    m: 'text/x-rsrc',
    cm: COMMENT_SHELL_LIKE,
  },
  Ruby: {
    x: 'rb',
    f: ['rspec', 'cw-2'],
    p: true,
    cm: COMMENT_RUBY_LIKE,
    filenames: ['Gemfile'],
    ls: ['solargraph'],
  },
  Rust: {
    x: 'rs',
    f: ['rust'],
    m: 'text/x-rustsrc',
  },
  Sass: {
    m: 'text/x-sass',
    n: 'Sass (SASS flavor)',
    ac: true,
    c: false,
    ls: ['css-language-server'],
  },
  Scss: {
    m: 'text/x-scss',
    n: 'Sass (SCSS Flavor)',
    ac: true,
    c: false,
    ls: ['css-language-server'],
  },
  Scala: {
    x: 'scala',
    f: ['junit', 'scalatest'],
    m: 'text/x-scala',
  },
  Shell: {
    m: 'text/x-sh',
    f: ['rspec'],
    fl: ['ruby'],
    x: 'sh',
    h: 'bash',
    cm: COMMENT_SHELL_LIKE,
  },
  SQL: {
    f: ['rspec'],
    fl: ['ruby'],
    // had to pick one, chose x-pgsql for now
    m: 'text/x-pgsql',
    cm: ['--', '/*', '*/'],
    ac: true,
    p: true,
  },
  Solidity: {
    x: 'sol',
    // Need to duplicate because `language.frameworkLanguage` uses the framework's index.
    fl: ['javascript', 'javascript'],
    f: ['mocha', 'truffle_with_mocha'],
  },
  SVG: {
    m: 'text/xml',
    c: false,
    cm: COMMENT_HTML,
  },
  Swift: {
    f: ['xctest'],
  },
  Text: {
    x: ['txt'],
    m: 'text/plain',
    c: false,
    filenames: ['Dockerfile'],
  },
  TSX: {
    m: 'text/typescript-jsx',
    c: false,
    h: 'typescript',
    l: 'typscriptreact',
    ls: ['typescript-language-server'],
  },
  TypeScript: {
    x: 'ts',
    f: ['jest', 'mocha_bdd', 'mocha_tdd', 'karma_bdd', 'karma_tdd'],
    m: 'text/typescript',
    p: true,
    ls: ['typescript-language-server'],
  },
  Vue: {
    x: 'vue',
    c: false,
    ac: true,
    m: 'text/x-vue',
    ls: ['vue-language-server'],
  },
  XML: {
    x: ['xslt'],
    m: 'text/xml',
    c: false,
    cm: COMMENT_HTML,
  },
  Yaml: {
    x: ['yml', 'yaml'],
    m: 'text/x-yaml',
    c: false,
    cm: COMMENT_SHELL_LIKE,
  },
};
export const fileMap = {};

const all = [];
const known = [];
const allExt = [];
const allNames = [];

angular.forEach(languages, function (lang, id) {
  function add(key) {
    if (!languages[key]) languages[key] = lang;
  }

  const idlc = id.toLowerCase();
  lang.id = idlc;
  lang.name = lang.namePlain = lang.n || id;
  lang.ext = lang.x || idlc;
  lang.codemirror = lang.mime = lang.m === undefined ? idlc : lang.m;
  lang.challenge = lang.c !== false;
  lang.highlight = lang.h || idlc;
  if (lang.f) {
    lang.frameworks = lang.f;
    // enables frameworks to use a different language
    lang.frameworkLanguages = lang.fl || lang.f.map(() => idlc);
  }
  lang.primary = !!lang.p;
  lang.cm = lang.cm || COMMENT_C_LIKE;
  lang.languageId = lang.l || lang.id;
  lang.languageServerIds = lang.ls || [];

  add(idlc);
  add(lang.name);
  add(lang.name.toLowerCase());

  if (!angular.isArray(lang.ext)) lang.ext = [lang.ext];
  allExt.push(...lang.ext);
  if (lang.filenames) {
    lang.filenames.forEach(add);
    allNames.push(...lang.filenames);
  }
  angular.forEach(lang.ext, add);

  known.push(lang);
  if (lang.challenge) {
    all.push(lang);
  }
});

// Potential sorting by primary / secondary, but then we need to deal with
// visually separating the results
export function langCompare(a, b) {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  if (a.primary && !b.primary) return -1;
  if (b.primary && !a.primary) return 1;
  return compareIgnoreCase(a.name || a, b.name || b);
}

// attempts to find a valid language based on several variations of the input
export function getLang(l) {
  return (
    l &&
    (languages[l] || // exact string match (l = 'js')
      languages[l.id] || // id match (l = {id:'js'})
      languages[l.name] || // name match (l = {name:'js'})
      languages[fileMap[l]] || // fileMapped by user
      languages[fileMap['*.' + getExtension(l)]]) // file extension mapped by user
  );
}

languages.primaryCompare = function (a, b) {
  return langCompare(getLang(a), getLang(b));
};

languages.compare = (a, b) => {
  if (a.id) {
    return compareIgnoreCase(a.name, b.name);
  } else if (a.name) {
    return compareIgnoreCase(languages[a.name].name, languages[b.name].name);
  } else {
    return compareIgnoreCase(a, b);
  }
};

languages.frameworkLanguage = (lang, framework) => {
  let l = getLang(lang);
  if (!l) {
    return (lang && lang.id) || lang;
  }
  let fwidx = l.frameworks.indexOf(framework);
  if (fwidx < 0) fwidx = 0;
  return l.frameworkLanguages[fwidx] || l.id;
};

languages.stripComments = (l, code) => {
  code = code || '';
  const lang = getLang(l);
  const cm = (lang && lang.cm) || [];
  if (cm[0]) {
    // single line comments
    code = code.replace(new RegExp(`${escapeRegex(cm[0])}.*$`, 'gm'), '');
  }
  if (cm[1]) {
    // multi line comments
    code = code.replace(
      new RegExp(`${escapeRegex(cm[1])}[\\s\\S]+?${escapeRegex(cm[2])}`, 'g'),
      '',
    );
  }
  // remove whitespace
  return code.trim();
};

languages.getLang = getLang;
languages.toName = (l) => (languages[l] ? languages[l].name : l);

all.sort(languages.compare);
languages.all = all;
known.sort(languages.compare);
languages.known = known;
languages.primary = all.slice().sort(languages.primaryCompare);
languages.frameworks = frameworks;
let VALID_EXT_REGEX;
languages.validExt = (name) => {
  if (!VALID_EXT_REGEX) {
    VALID_EXT_REGEX = new RegExp(
      `(^${allNames.join('|')})|(.(${allExt.join('|')}))$`,
    );
  }
  return VALID_EXT_REGEX.test(name);
};
languages.langFromFilename = (filename) => {
  const ext = getExtension(filename);
  let lang = ext && getLang(ext);
  if (!lang && filename) {
    // check to match full-name files
    filename = filename.replace(/^(.*\/)/g, '');
    lang = allNames.includes(filename) && getLang(filename);
  }
  return lang;
};

window.$languages = languages;
